import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, Menu, LogOut, Check, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TopBar = ({ onMenuClick, showMenuButton }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Hide bell icon on Dashboard - notifications are shown in carousel there
  const isDashboard = location.pathname === '/dashboard';

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      // Use dropdown endpoint - only unread, non-archived
      const response = await axios.get(`${API_URL}/api/notifications/dropdown`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, user]);

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notifId}/read?archive=false`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all?archive=false`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (e, notifId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/notifications/${notifId}`);
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: fr });
    } catch {
      return '';
    }
  };

  // Get notification type color
  const getNotificationColor = (type) => {
    switch (type) {
      case 'cours_supprime':
        return 'border-l-red-500 bg-red-50';
      case 'desinscription_par_instructeur':
        return 'border-l-orange-500 bg-orange-50';
      case 'absence_urgente':
        return 'border-l-amber-500 bg-amber-50';
      default:
        return 'border-l-blue-500 bg-blue-50';
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-stone-200" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="flex items-center justify-between px-4 py-3 md:px-6">
        {/* Left section - Mobile menu button */}
        <div className="w-10 md:hidden">
          {showMenuButton && (
            <button
              className="p-2 -ml-2 rounded-lg hover:bg-stone-100"
              onClick={onMenuClick}
              data-testid="mobile-menu-btn"
              aria-label="Ouvrir le menu"
            >
              <Menu size={24} className="text-[#2E4A3D]" />
            </button>
          )}
        </div>
        
        {/* Center - Logo for mobile */}
        <h1 className="md:hidden text-xl font-serif font-bold text-[#2E4A3D] absolute left-1/2 -translate-x-1/2">
          Equibook
        </h1>
        
        {/* Desktop page title */}
        <div className="hidden md:block">
          <h2 className="text-lg font-medium text-stone-800">Bienvenue, {user?.prenom}</h2>
        </div>
        
        {/* Right section - Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications - hidden on Dashboard */}
          {!isDashboard && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="relative p-2 rounded-lg hover:bg-stone-100 transition-colors"
                data-testid="notifications-btn"
              >
                <Bell size={22} strokeWidth={1.5} className="text-stone-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#E07A5F] text-white text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-96 max-h-[70vh] overflow-y-auto">
              <div className="p-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <h3 className="font-semibold text-sm">Notifications</h3>
                {unreadCount > 0 && (
                  <button 
                    onClick={markAllAsRead}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <Check size={12} />
                    Tout marquer comme lu
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell size={32} className="mx-auto text-stone-300 mb-2" />
                  <p className="text-sm text-stone-500">Aucune notification</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.id}
                      className={`p-3 cursor-pointer hover:bg-stone-50 transition-colors border-l-4 ${
                        !notif.lu ? getNotificationColor(notif.type) : 'border-l-transparent'
                      }`}
                      onClick={() => !notif.lu && markAsRead(notif.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notif.lu ? 'font-semibold text-stone-900' : 'text-stone-700'}`}>
                            {notif.titre}
                          </p>
                          <p className="text-xs text-stone-600 mt-1 line-clamp-2">
                            {notif.message}
                          </p>
                          <p className="text-xs text-stone-400 mt-1">
                            {formatTime(notif.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {!notif.lu && (
                            <div className="w-2 h-2 rounded-full bg-[#E07A5F] flex-shrink-0" />
                          )}
                          <button
                            onClick={(e) => deleteNotification(e, notif.id)}
                            className="p-1 hover:bg-stone-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Supprimer"
                          >
                            <Trash2 size={14} className="text-stone-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <DropdownMenuSeparator />
              <div className="p-2">
                <button
                  onClick={() => navigate('/notifications')}
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-800 py-2 hover:bg-stone-50 rounded"
                >
                  Voir toutes les notifications
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          
          {/* User menu - desktop */}
          <div className="hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-stone-100 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center text-sm font-medium">
                    {user?.prenom?.[0]}{user?.nom?.[0]}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut size={16} className="mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};
